/**
 * Can this browser actually refract?
 *
 * True Apple-style liquid glass needs an SVG filter used as an input to
 * `backdrop-filter`, which as of 2026 is Chromium-only — Safari and Firefox
 * implement `backdrop-filter` but not `url()` inside it. There is no clean
 * `@supports` answer, because a browser can parse the syntax it cannot render,
 * so this tests the property the only way that is honest and then lets the CSS
 * decide.
 *
 * WHY BEING WRONG IS CHEAP HERE
 * The refraction is painted by a separate inset overlay
 * (`.iv-glass-refract-layer`), never by the panel's own `backdrop-filter`. A
 * false positive therefore renders an invisible empty layer over an unchanged
 * glass panel — not a chat window that has lost its blur and gone unreadable.
 * That property is why the detection is allowed to be imperfect.
 */

const REFRACT_CLASS = 'iv-glass-refract';

export function supportsBackdropRefraction(): boolean {
  if (typeof window === 'undefined' || typeof CSS === 'undefined' || !CSS.supports) {
    return false;
  }
  // Both spellings: WebKit-prefixed browsers answer only the prefixed one.
  const parses =
    CSS.supports('backdrop-filter', 'url(#iv-glass-displace)') ||
    CSS.supports('-webkit-backdrop-filter', 'url(#iv-glass-displace)');
  if (!parses) {
    return false;
  }
  // Someone who has asked for reduced transparency has asked for LESS of this,
  // not a fancier version of it.
  if (window.matchMedia?.('(prefers-reduced-transparency: reduce)').matches) {
    return false;
  }
  return true;
}

/**
 * Flag the document once, at startup.
 *
 * On the root element rather than on the widget, so the filter can be defined
 * once and any future glass surface picks the same answer up without re-testing.
 */
export function applyGlassRefractionClass(): void {
  if (typeof document === 'undefined') {
    return;
  }
  document.documentElement.classList.toggle(REFRACT_CLASS, supportsBackdropRefraction());
}
